/**
 * AI metadata extraction — turns raw PNG chunks into a normalized,
 * UI-friendly view model. Keeps vendor heuristics separate from the
 * low-level chunk parser (Codex 2.B.1.design D5: distinct test seam).
 *
 * Recognized generators (v1):
 *   - A1111 / Forge:  tEXt with keyword `parameters` (free-form text)
 *   - ComfyUI:        tEXt or iTXt with keyword `prompt` and/or
 *                     `workflow` (JSON payloads)
 *   - Midjourney:     tEXt with keyword `Description` (the prompt is
 *                     embedded as the description)
 *   - Generic:        any other AI keyword from png-metadata.AI_KEYWORDS
 *                     surfaces as "other"
 *
 * Per Codex HIGH-1: this module produces a NORMALIZED view model only.
 * It does NOT decide what to preserve on export — that's
 * `filterAIChunks` in png-metadata.js operating on raw chunk bytes.
 */

import { listChunks, parseTEXt, parseITXt } from './png-metadata.js';

/**
 * @typedef {object} AIMetadata
 * @prop {boolean} present              true if any AI-related chunk found
 * @prop {string | null} vendor         'a1111' | 'comfyui' | 'midjourney' | 'unknown' | null
 * @prop {string | null} model          model name extracted from parameters (best-effort)
 * @prop {string | null} prompt         positive prompt text
 * @prop {string | null} negativePrompt negative prompt text (A1111)
 * @prop {string | null} sampler        sampler name (A1111)
 * @prop {number | null} seed
 * @prop {number | null} steps
 * @prop {number | null} cfgScale
 * @prop {object | null} workflow       parsed ComfyUI workflow JSON
 * @prop {object} other                 unrecognized but extracted text chunks (keyword → string)
 * @prop {Array<{ type, keyword }>} chunkSummary  for UI badge / debug
 */

/**
 * Extract a normalized AI metadata struct from a PNG byte buffer.
 * Returns `{ present: false, ... }` when no AI-related chunks are
 * found OR when the buffer isn't a PNG (silent — caller decides).
 *
 * @param {Uint8Array} pngBytes
 * @returns {AIMetadata}
 */
export function extractAIMetadata(pngBytes) {
    const empty = makeEmpty();
    if (!pngBytes) return empty;
    let chunks;
    try { chunks = listChunks(pngBytes); }
    catch { return empty; }

    /* Walk chunks once, gather text payloads keyed by keyword. */
    const text = new Map();   /* keyword → string */
    const chunkSummary = [];
    for (const ch of chunks) {
        if (ch.type !== 'tEXt' && ch.type !== 'iTXt') continue;
        let parsed;
        if (ch.type === 'tEXt') parsed = parseTEXt(ch.data);
        else                     parsed = parseITXt(ch.data);
        if (!parsed || !parsed.keyword) continue;
        chunkSummary.push({ type: ch.type, keyword: parsed.keyword });
        /* Only first occurrence wins (PNG spec allows duplicates only
         * for some keys; first-occurrence is the simplest contract). */
        if (!text.has(parsed.keyword)) {
            text.set(parsed.keyword, parsed.text || '');
        }
    }
    if (chunkSummary.length === 0) return empty;

    /* Vendor detection — order matters: ComfyUI is checked BEFORE
     * "Description" because some MJ exports include both keywords. */
    let vendor = null;
    if (text.has('parameters')) vendor = 'a1111';
    else if (text.has('prompt') || text.has('workflow')) vendor = 'comfyui';
    else if (text.has('Description')) vendor = 'midjourney';
    else vendor = 'unknown';

    const meta = {
        present: true,
        vendor,
        model: null,
        prompt: null,
        negativePrompt: null,
        sampler: null,
        seed: null,
        steps: null,
        cfgScale: null,
        workflow: null,
        other: Object.create(null),
        chunkSummary,
    };

    /* A1111 / Forge "parameters" parsing.
     *
     * The de-facto format is:
     *   <prompt>\nNegative prompt: <negPrompt>\n<key: value, key2: value2, ...>
     * It's free-form, but reliably anchored on "Negative prompt:" and a
     * trailing line of comma-separated metadata. */
    if (text.has('parameters')) {
        parseA1111(text.get('parameters'), meta);
    }

    /* ComfyUI "prompt" + "workflow" — both are JSON. */
    if (text.has('workflow')) {
        try { meta.workflow = JSON.parse(text.get('workflow')); }
        catch { /* malformed JSON — keep workflow null but vendor tag stays */ }
    }
    if (text.has('prompt')) {
        /* For ComfyUI the "prompt" key is a node-graph JSON, NOT a
         * free-form prompt string. Try JSON parse; if it succeeds we
         * walk the graph for prompt + model + sampler/seed/steps/cfg.
         * 2026-05-07 fix: previously only extracted prompt text from
         * `CLIPTextEncode`. z-image and other custom workflows use
         * encoder classes like `Z_ImageTextEncode`, `T5TextEncode`, so
         * we now match any `class_type` containing `TextEncode`. Also
         * extract model from CheckpointLoader/UNETLoader and KSampler
         * params (seed/steps/cfg_scale/sampler_name). */
        const raw = text.get('prompt');
        try {
            const j = JSON.parse(raw);
            if (!meta.prompt) {
                const found = findComfyPromptText(j);
                if (found) meta.prompt = found;
            }
            if (!meta.model)    meta.model    = findComfyModel(j);
            const ks = findComfyKSampler(j);
            if (ks) {
                if (meta.seed     == null && ks.seed     != null) meta.seed     = ks.seed;
                if (meta.steps    == null && ks.steps    != null) meta.steps    = ks.steps;
                if (meta.cfgScale == null && ks.cfgScale != null) meta.cfgScale = ks.cfgScale;
                if (meta.sampler  == null && ks.sampler  != null) meta.sampler  = ks.sampler;
            }
        } catch {
            /* Plain string prompt? Some MJ exports use this key too. */
            if (raw && raw.trim() && !meta.prompt) meta.prompt = raw.trim();
        }
    }
    /* Same walkers on the workflow JSON as a fallback when no `prompt`
     * chunk is present (some Comfy custom-node setups emit only one). */
    if (vendor === 'comfyui' && meta.workflow) {
        const wfGraph = meta.workflow.nodes || meta.workflow;
        if (!meta.prompt) {
            const found = findComfyPromptText(wfGraph);
            if (found) meta.prompt = found;
        }
        if (!meta.model) meta.model = findComfyModel(wfGraph);
        const ks = findComfyKSampler(wfGraph);
        if (ks) {
            if (meta.seed     == null && ks.seed     != null) meta.seed     = ks.seed;
            if (meta.steps    == null && ks.steps    != null) meta.steps    = ks.steps;
            if (meta.cfgScale == null && ks.cfgScale != null) meta.cfgScale = ks.cfgScale;
            if (meta.sampler  == null && ks.sampler  != null) meta.sampler  = ks.sampler;
        }
    }

    /* MJ "Description" — usually the original prompt as plain text. */
    if (vendor === 'midjourney' && text.has('Description') && !meta.prompt) {
        meta.prompt = text.get('Description');
    }

    /* Fold every unrecognized keyword into `other` for UI display. */
    for (const [k, v] of text.entries()) {
        if (k === 'parameters' || k === 'prompt' || k === 'workflow'
                || k === 'Description' || k === 'Negative prompt') continue;
        meta.other[k] = v;
    }

    return meta;
}

function makeEmpty() {
    return {
        present: false,
        vendor: null,
        model: null,
        prompt: null,
        negativePrompt: null,
        sampler: null,
        seed: null,
        steps: null,
        cfgScale: null,
        workflow: null,
        other: Object.create(null),
        chunkSummary: [],
    };
}

/* Parse the A1111 / Forge "parameters" free-form text.
 * Format example:
 *   a beautiful landscape, golden hour
 *   Negative prompt: blurry, low quality
 *   Steps: 30, Sampler: DPM++ 2M Karras, CFG scale: 7, Seed: 12345,
 *   Size: 768x1024, Model: sd_xl_base_1.0
 *
 * We split on the "Negative prompt:" anchor (if present) and then on
 * the LAST blank-or-newline boundary before the metadata line. */
function parseA1111(raw, meta) {
    if (!raw) return;
    const text = raw.replace(/\r/g, '');
    let prompt = '';
    let negative = '';
    let metaLine = '';
    /* Try to find a "Negative prompt:" header. Anchored to start of line. */
    const negIdx = text.search(/(^|\n)Negative prompt:\s*/);
    if (negIdx >= 0) {
        prompt = text.slice(0, negIdx).trim();
        const after = text.slice(negIdx).replace(/^[\n\r]?Negative prompt:\s*/, '');
        /* The metadata line is the LAST trailing line that starts with
         * a recognizable key (Steps:, Sampler:, etc.) — find its start. */
        const metaIdx = after.search(/(^|\n)(Steps:|Sampler:|CFG scale:|Seed:|Size:|Model:|Model hash:)/);
        if (metaIdx >= 0) {
            negative = after.slice(0, metaIdx).trim();
            metaLine = after.slice(metaIdx).replace(/^\n?/, '');
        } else {
            negative = after.trim();
        }
    } else {
        /* No negative prompt — split on metadata-keys anchor. */
        const metaIdx = text.search(/(^|\n)(Steps:|Sampler:|CFG scale:|Seed:|Size:|Model:|Model hash:)/);
        if (metaIdx >= 0) {
            prompt = text.slice(0, metaIdx).trim();
            metaLine = text.slice(metaIdx).replace(/^\n?/, '');
        } else {
            prompt = text.trim();
        }
    }
    meta.prompt = prompt || null;
    meta.negativePrompt = negative || null;
    if (metaLine) parseA1111MetaLine(metaLine, meta);
}

function parseA1111MetaLine(line, meta) {
    /* The metadata line is comma-separated `key: value` pairs. Values
     * may contain commas inside quoted strings; we scan tokens at
     * top-level using a small state machine. */
    const tokens = splitTopLevelCommas(line);
    for (const tok of tokens) {
        const idx = tok.indexOf(':');
        if (idx <= 0) continue;
        const key = tok.slice(0, idx).trim();
        const val = tok.slice(idx + 1).trim().replace(/^"|"$/g, '');
        switch (key) {
            case 'Steps':       meta.steps    = parseIntOr(val, null); break;
            case 'Sampler':     meta.sampler  = val || null;            break;
            case 'CFG scale':   meta.cfgScale = parseFloatOr(val, null);break;
            case 'Seed':        meta.seed     = parseIntOr(val, null); break;
            case 'Model':       meta.model    = val || null;            break;
            case 'Model hash':  if (!meta.model) meta.model = val;      break;
            default:
                /* Other keys go into `other` so the UI can show them. */
                meta.other[key] = val;
        }
    }
}

function splitTopLevelCommas(s) {
    const out = [];
    let depth = 0, inStr = false, start = 0;
    for (let i = 0; i < s.length; i++) {
        const c = s[i];
        if (c === '"' && (i === 0 || s[i - 1] !== '\\')) inStr = !inStr;
        else if (!inStr && (c === '(' || c === '[' || c === '{')) depth++;
        else if (!inStr && (c === ')' || c === ']' || c === '}')) depth--;
        else if (!inStr && depth === 0 && c === ',') {
            out.push(s.slice(start, i));
            start = i + 1;
        }
    }
    if (start < s.length) out.push(s.slice(start));
    return out;
}

function parseIntOr(v, fallback) {
    const n = parseInt(v, 10);
    return Number.isFinite(n) ? n : fallback;
}
function parseFloatOr(v, fallback) {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : fallback;
}

/* Walk a ComfyUI prompt-graph JSON looking for the positive prompt text.
 * 2026-05-07 fix: matches any `class_type` containing `TextEncode` so
 * z-image / Tencent / Flux / SD3 / Hunyuan custom encoders all work, not
 * just stock `CLIPTextEncode`. Returns the first non-empty `inputs.text`
 * (heuristic — Comfy doesn't tag positive vs negative; most stock
 * workflows put the positive encoder first). */
function findComfyPromptText(graph) {
    if (!graph || typeof graph !== 'object') return null;
    /* ComfyUI `prompt` chunk format: { "<nodeId>": { class_type, inputs: { text, ... } } } */
    /* Workflow chunk format may be { nodes: [...] } — caller normalizes. */
    const iter = Array.isArray(graph) ? graph : Object.values(graph);
    for (const node of iter) {
        if (!node || typeof node !== 'object') continue;
        const cls = node.class_type || node.type;
        if (typeof cls !== 'string') continue;
        if (!/TextEncode/i.test(cls)) continue;
        const t = node.inputs && node.inputs.text;
        if (typeof t === 'string' && t.trim()) return t;
    }
    return null;
}

/* Walk a ComfyUI graph for the checkpoint/UNET/model name. Stock
 * workflows put it on `CheckpointLoaderSimple.inputs.ckpt_name`;
 * z-image / SD3 / Flux variants use `UNETLoader.inputs.unet_name`,
 * `ModelLoader.inputs.model_name`, etc. Returns null if no loader
 * found. 2026-05-07 fix: previously the model field stayed null for
 * every ComfyUI image, blocking diffusion-cast pattern matching. */
function findComfyModel(graph) {
    if (!graph || typeof graph !== 'object') return null;
    const iter = Array.isArray(graph) ? graph : Object.values(graph);
    for (const node of iter) {
        if (!node || typeof node !== 'object') continue;
        const cls = node.class_type || node.type;
        if (typeof cls !== 'string') continue;
        if (!/(?:Checkpoint|UNET|Model|Diffusion).*Loader/i.test(cls)) continue;
        const inp = node.inputs;
        if (!inp || typeof inp !== 'object') continue;
        const candidate = inp.ckpt_name || inp.unet_name || inp.model_name
            || inp.diffusion_model || inp.model;
        if (typeof candidate === 'string' && candidate.trim()) return candidate;
    }
    return null;
}

/* Walk a ComfyUI graph for the first KSampler-class node and extract
 * sampling params. Covers KSampler / KSamplerAdvanced / SamplerCustom
 * variants used by stock + custom workflows. KSamplerAdvanced uses
 * `noise_seed` (not `seed`); fall back so both shapes work. */
function findComfyKSampler(graph) {
    if (!graph || typeof graph !== 'object') return null;
    const iter = Array.isArray(graph) ? graph : Object.values(graph);
    for (const node of iter) {
        if (!node || typeof node !== 'object') continue;
        const cls = node.class_type || node.type;
        if (typeof cls !== 'string') continue;
        if (!/Sampler/i.test(cls)) continue;
        const inp = node.inputs;
        if (!inp || typeof inp !== 'object') continue;
        const seedRaw = inp.seed != null ? inp.seed : inp.noise_seed;
        return {
            seed:     parseIntOr(seedRaw,      null),
            steps:    parseIntOr(inp.steps,    null),
            cfgScale: parseFloatOr(inp.cfg,    null),
            sampler:  typeof inp.sampler_name === 'string' ? inp.sampler_name : null,
        };
    }
    return null;
}
